import styled from 'styled-components';

import { PRIMARY, TEXT } from 'constants/colors';

import { rSize } from 'components/utils';

export default styled.div`
  h2 {
    font-family: 'SourceSansPro-Bold', Arial, 'sans-serif';
    font-size: ${rSize(19)};
  }

  h2,
  h3,
  h4,
  p {
    margin-top: 0;
    margin-bottom: 10px;
  }

  .planunit-selector {
    margin-bottom: 20px;
  }

  .widgets {
    display: flex;
    justify-content: space-between;
    gap: 20px;
  }

  .left-side {
    flex-basis: 50%;
    flex-grow: 1;
  }

  .right-side {
    flex-basis: 50%;
  }

  .open-requests {
    /* cijfers krijgen dezelfde kleur als de tekst links op de regel */
    --qm-total-fg: ${TEXT};
    --qm-sub-fg: var(--mui-palette-text-secondary);
    --qm-empty: #9ca3af;
    /* hover: laat zien dat het cijfer klikbaar is */
    --qm-chip-hover-bg: rgba(2, 56, 80, 0.08);
    /* totaalregel (naam + cijfers) exact als de widgettitel: 16px
       (theme.typography.htmlFontSize) in SourceSansPro-Bold — bold is in deze
       app een familie, niet een font-weight (zie baseStyling) */
    --qm-total-fs: 16px;
    /* subregels op dezelfde maat; het niveauverschil zit in familie en kleur */
    --qm-sub-fs: var(--qm-total-fs);
    /* vandaag: zelfde date-label als de roster view (ronde PRIMARY-vulling) */
    --qm-today-label-bg: ${PRIMARY};
    --qm-today-label-fg: #ffffff;
    /* ponytail: opaque i.p.v. 2% alpha — de headercel is sticky, door een
       doorzichtige tint scrollen de rijen eronder zichtbaar door de header */
    --qm-col-weekend-bg: #fafafb;

    width: 100%;
    padding: ${rSize(24)};
    box-sizing: border-box;
    display: inline-flex;
    flex-direction: column;
    place-self: flex-start;
    margin-bottom: ${rSize(20)};

    h2 {
      margin-bottom: ${rSize(20)};
    }

    .MuiTableContainer-root {
      th.MuiTableCell-root.MuiTableCell-head {
        box-shadow: 0 -1px 0 0 #d8d8d8 inset;
      }
      width: auto;

      .is-saturday,
      .is-sunday {
        background-color: var(--qm-col-weekend-bg);
      }

      /* de naamkolom en de twee dagtabellen zijn losse <table>s die rij voor rij
         gelijk moeten lopen: vaste hoogtes, want een responsieve clamp zakt bij
         inzoomen naar zijn minimum en dan bepaalt de inhoud de rijhoogte —
         per tabel verschillend, en dan lopen de lijnen scheef */
      th,
      td {
        padding: 0 ${rSize(10)};
        height: 50px;
        box-sizing: border-box;
      }

      /* naam en cijfers van één regel altijd op dezelfde maat; een <button>
         erft het celfont niet vanzelf, vandaar font: inherit */
      .MuiTableBody-root th,
      .MuiTableBody-root td {
        font-size: var(--qm-total-fs);
        font-family: 'SourceSansPro-Bold';
        font-weight: normal;
      }

      .MuiTableBody-root .is-breakdown th,
      .MuiTableBody-root .is-breakdown td {
        font-size: var(--qm-sub-fs);
        font-family: 'SourceSansPro-Regular';
      }

      button.plain-btn {
        font: inherit;
      }

      .MuiTableHead-root {
        th {
          height: 59px;
          /* dagafkorting en datum op één maat, gelijk aan de regels eronder */
          font-size: var(--qm-total-fs);
        }

        .date-label {
          border-radius: 50%;
          width: ${rSize(30)};
          height: ${rSize(30)};
          display: inline-flex;
          justify-content: center;
          align-items: center;
        }

        .is-today .date-label {
          background-color: var(--qm-today-label-bg);
          color: var(--qm-today-label-fg);
        }

        .is-saturday {
          border-top-left-radius: 16px;
        }

        .is-sunday {
          border-top-right-radius: 16px;
        }

        .is-saturday,
        .is-sunday {
          box-shadow:
            0 -1px 0 0 #d8d8d8 inset,
            0 0 0 10px #ffffff !important;
        }
      }

      /* striping per planeenheid (is-striped), niet per rijpositie: met
         uitgeklapte subregels klapt anders elke regel eronder van kleur */
      .MuiTableBody-root .MuiTableRow-root.is-striped {
        background-color: var(--mui-palette-grey-50);
      }

      .MuiTableBody-root .MuiTableRow-root:last-child {
        th,
        td {
          border-bottom: none;
        }
        .is-saturday {
          border-bottom-left-radius: 16px;
        }

        .is-sunday {
          border-bottom-right-radius: 16px;
        }
      }

      /* streepjes (lege waarden) — chips zetten hun eigen kleur */
      .MuiTableBody-root td {
        color: var(--qm-empty);
      }

      .MuiChip-root {
        /* Chip zet zelf 0.8125rem (small: 0.75rem) — overrulen op de root, want
           het label erft van de chip, niet van de cel */
        font: inherit;
        width: ${rSize(30)};
        height: ${rSize(30)};
        border: 0;
        background-color: transparent;
        color: var(--qm-total-fg);

        /* eigen hover: de transparante achtergrond hierboven overrulet die van
           MuiChip-clickable */
        &:hover,
        &:focus-visible {
          background-color: var(--qm-chip-hover-bg);
        }

        /* subregels: zelfde secundaire kleur als hun categorielabel links */
        &.MuiChip-outlined {
          color: var(--qm-sub-fg);
        }

        .MuiChip-label {
          /* zelfde font als de naam op dezelfde regel; de maat komt van de cel */
          font: inherit;
          text-overflow: unset;
          /* vast font in een responsieve chip: laat 2 cijfers liever uitsteken
             dan clippen — de chip heeft geen vulling meer */
          overflow: visible;
          padding: 0;

          .icon-spinner {
            color: ${PRIMARY};
          }
        }
      }

      &:first-child {
        .MuiTableCell-body {
          width: ${rSize(220)};
          /* height is in een tabelcel een minimum: een afbrekende naam duwt de
             rij alsnog hoger dan de dagtabellen ernaast */
          white-space: nowrap;
          overflow: hidden;
        }

        button {
          text-align: left;
          display: inline-block;
          position: relative;
          overflow: hidden;
          text-overflow: ellipsis;

          &:hover {
            text-decoration: underline;
          }

          & + .icon-spinner {
            margin-left: 10px;
          }
        }
      }

      &:first-child {
        flex-grow: 1;
      }

      &:nth-child(2) {
        .MuiTableCell-root {
          width: ${rSize(40)};
        }
      }

      &:last-child {
        .MuiTableCell-root {
          width: ${rSize(40)};
        }
      }
    }
  }

  @media (max-width: 1100px) {
    .widgets {
      flex-direction: column;
    }
    .open-requests,
    .task-status {
      width: 100%;
    }
    .open-requests {
      margin-bottom: ${rSize(40)};
    }
  }
`;
